import { connect } from 'react-redux';
import { useEffect, useState, useCallback } from 'react';
import axios from 'axios';

import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";




import LeagueConflictError from './LeagueConflictError';

// Import app configuration for Discord URL
import { appConfig } from '../../config/app.js';

const PlanSelection = ({auth}) => {        
    const step_number = 5; // Plan selection is step 5
    const [isProcessing, setIsProcessing] = useState(true);
    const [hasRequiredData, setHasRequiredData] = useState(false);
    const [selectedLeagues, setSelectedLeagues] = useState([]);
    const [leagues, setLeagues] = useState([]);
    const [leagueConflictError] = useState(null);
    const [error, setError] = useState('');
    const [isPaymentLoading, setIsPaymentLoading] = useState(false);

    useEffect(() => {                
        const checkOnboardingData = () => {
            // If user is authenticated and has permissions, allow access regardless of localStorage
            if (auth?.user?.permission) {
                // User is authenticated, allow access to step 8
                // Add small delay for smooth transition
                setTimeout(() => {
                    setHasRequiredData(true);
                    setIsProcessing(false);
                }, 800);
            } else {
                // Unauthenticated user should not be on plan selection
                window.location.href = '/onboarding/platform';
                return;
            }
        };

        checkOnboardingData();
    }, [auth]);

    // Load leagues data from database via API
    const loadLeaguesDataFromAPI = useCallback(async () => {
        try {
            const response = await axios.get('/api/get/user-leagues');
            
            if (response.data && response.data.leagues) {
                const leaguesData = response.data.leagues;
                
                // Transform the data to match the expected format
                const transformedLeagues = leaguesData.map(league => ({
                    id: league.id,
                    name: league.name,
                    platform_league_id: league.platform_league_id,
                    platform_user_id: league.platform_user_id,
                    username: league.username,
                    display_name: league.display_name,
                    team_name: league.team_name,
                    platform_name: league.platform_name
                }));
                
                setLeagues(transformedLeagues);
                // Set all leagues as selected since user has already chosen them
                setSelectedLeagues(transformedLeagues.map(league => league.id));
            }
        } catch (error) {
            console.error('❌ Error fetching user leagues from API:', error);
            // Fallback to localStorage data if API fails
            loadLeaguesDataFromStorage();
        }
    }, []);

    // Post-authentication effect: handle localStorage data and database updates
    useEffect(() => {
        if (!hasRequiredData || !auth?.user?.permission) return;
        
        const handlePostAuthentication = async () => {
            // League data should already be migrated by OAuth callbacks
            // Just load the leagues data for display
            try {
                console.log('📋 PlanSelection: Loading leagues data (migration should be complete from OAuth)');
                loadLeaguesDataFromAPI();
                setIsProcessing(false);
            } catch (error) {
                console.error('❌ PlanSelection: Error loading leagues data:', error);
                setError('Failed to load league data. Please contact support at support@recapvid.com or reach out to us in Discord.');
                setIsProcessing(false);
            }
        };
        
        handlePostAuthentication();
    }, [hasRequiredData, auth, loadLeaguesDataFromAPI]);
    
    // Load leagues data from localStorage (fallback)
    const loadLeaguesDataFromStorage = (onboardingData) => {
        if (onboardingData && onboardingData.leagues) {
            const leaguesData = onboardingData.leagues;
            if (leaguesData.selectedLeagues && leaguesData.totalLeagues) {
                // Use the actual league data from onboarding if available
                if (leaguesData.leagues && Array.isArray(leaguesData.leagues)) {
                    setLeagues(leaguesData.leagues);
                    setSelectedLeagues(leaguesData.selectedLeagues);
                }
            }
        }
    };
    
    // Load leagues data when component is ready
    useEffect(() => {
        if (!hasRequiredData) return;
        
        const loadLeaguesData = () => {
            // Load leagues data from database via API
            loadLeaguesDataFromAPI();
        };
        
        loadLeaguesData();
    }, [hasRequiredData, auth, loadLeaguesDataFromAPI]);

    useEffect(() => {
        if (!hasRequiredData) return;
        
        const setOnboardingStep = async () => {                   
            try {
                // First, try to create permission record if it doesn't exist
                if (auth?.user?.id && !auth?.user?.permission) {
                    try {
                        await axios.post('/api/post/create-permission-record', {
                            user_id: auth.user.id,
                            onboarding_step: step_number
                        });
                    } catch (createError) {
                        // Permission record may already exist, continuing...
                    }
                }
                
                // No need to save to localStorage - database is the source of truth
            } catch (error) {
                console.error('Error setting onboarding step:', error);
            }
        }

        setOnboardingStep();
        

        
    }, [hasRequiredData, auth, step_number]);





    const handleLeagueToggle = (leagueId) => {
        setSelectedLeagues(prev => 
            prev.includes(leagueId) 
                ? prev.filter(id => id !== leagueId)
                : [...prev, leagueId]
        );
    };

    const handlePaymentSubmit = () => {
        setIsPaymentLoading(true);
        
        try {
            // Trigger Stripe Plus plan checkout using form submission (as originally designed)
            const form = document.createElement('form');
            form.method = 'POST';
            form.action = '/api/post/stripe/create-checkout-session';
            
            const input = document.createElement('input');
            input.type = 'hidden';
            input.name = 'lookup_key';
            input.value = 'standard_monthly'; // Use Standard monthly plan
            
            form.appendChild(input);
            document.body.appendChild(form);
            form.submit();
            
            // Note: We don't reset loading state here because the page will redirect
            // If there's an error, the user will be redirected back and can try again
        } catch (error) {
            console.error('Error submitting payment form:', error);
            // Reset loading state on error
            setIsPaymentLoading(false);
        }
    };



    const getProgressPercentage = () => {
        // Plan selection is step 8 of 8, so (8-1)/8 = 87.5%
        return 87.5;
    };

    // Show processing state while checking onboarding data
    if (isProcessing) {
        return (
            <div className="pt-5 onboarding-section">    
                <Row className="onboarding-header">
                    <Col xs={1} md={2}></Col>
                    <Col xs={10} md={8}>       
                            
                        <Row className="text-center onboarding-title-row">
                            <Col className="onboarding-title">Setting up your account...</Col>
                        </Row>
                        
                        <Row className="mt-4 text-center">
                            <Col>
                                <div className="text-center">
                                    <div className="spinner-border text-primary" role="status" style={{ width: "3rem", height: "3rem" }}>
                                        <span className="visually-hidden">Loading...</span>
                                    </div>

                                    
                                    {/* Progress bar for visual feedback */}
                                    <div className="mt-4" style={{ maxWidth: "300px", margin: "0 auto" }}>
                                        <div className="onboarding-progress">
                                            <div 
                                                className="onboarding-progress-bar"
                                                style={{ width: `${getProgressPercentage()}%` }}
                                            ></div>
                                        </div>
                                    </div>
                                </div>
                            </Col>
                        </Row>
                    </Col>
                </Row>
            </div>
        );
    }

    // Show error message if there's an error
    if (error) {
        return (
            <div className="pt-5 onboarding-section">    
                <Row className="onboarding-header">
                    <Col xs={1} md={2}></Col>
                    <Col xs={10} md={8}>       
                            
                        <Row className="text-center onboarding-title-row">
                            <Col className="onboarding-title">Something went wrong</Col>
                        </Row>
                        
                        <Row className="mt-4 text-center">
                            <Col>
                                <div className="text-center">
                                    <p className="text-white">
                                        {error.split('Discord')[0]}
                                        <a 
                                            href={appConfig.social.discord} 
                                            target="_blank" 
                                            rel="noopener noreferrer"
                                            style={{ color: '#ffffff', textDecoration: 'underline' }}
                                        >
                                            Discord
                                        </a>
                                        {error.includes('Discord') ? error.split('Discord')[1] : ''}
                                    </p>
                                </div>
                            </Col>
                        </Row>
                    </Col>
                </Row>
            </div>
        );
    }

    // Show league conflict error if one exists
    if (leagueConflictError) {
        return <LeagueConflictError errorData={leagueConflictError} />;
    }

    return (
        <>
            <div className="pt-5 onboarding-section">    
                <Row className="onboarding-header">
                    <Col xs={1} md={2}></Col>
                    <Col xs={10} md={8}>       
                            
                        <Row className="text-center onboarding-title-row">
                            <Col className="onboarding-title">Choose Your Plan</Col>
                        </Row>
                        
                        {/* Progress Bar */}
                        <Row className="mt-4">
                            <Col>
                                <div className="text-center mb-1">
                                    <span className="onboarding-progress-percentage">
                                        {Math.round(getProgressPercentage())}% Complete
                                    </span>
                                </div>
                                <div className="onboarding-progress">
                                    <div 
                                        className="onboarding-progress-bar"
                                        style={{ width: `${getProgressPercentage()}%` }}
                                    ></div>
                                </div>
                            </Col>
                        </Row>   
                        <Row className="pt-2">
                            <Col>
                                {/* League Selection and Pricing Display */}
                                {leagues.length > 0 ? (
                                    <Row className="mb-1">
                                        <Col>
                                            <div className="league-options mb-2">
                                                {leagues.map(league => (
                                                    <div 
                                                        key={league.id} 
                                                        className={`league-card mb-2 ${selectedLeagues.includes(league.id) ? 'selected' : 'unselected'}`}
                                                        onClick={() => handleLeagueToggle(league.id)}
                                                        onKeyDown={(e) => {
                                                            if (e.key === 'Enter' || e.key === ' ') {
                                                                e.preventDefault();
                                                                handleLeagueToggle(league.id);
                                                            }
                                                        }}
                                                        role="button"
                                                        tabIndex={0}
                                                        aria-label={`Select ${league.name} league`}
                                                        aria-pressed={selectedLeagues.includes(league.id)}
                                                        style={{ 
                                                            padding: '0.75rem',
                                                            minHeight: '60px',
                                                            marginTop: '8px',
                                                            marginBottom: '16px'
                                                        }}
                                                    >
                                                        <div className="text-center d-flex align-items-center justify-content-center">
                                                            <h5 className="league-name" style={{ fontSize: '1rem', margin: '0' }}>{league.name}</h5>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        </Col>
                                    </Row>
                                ) : (
                                    <Row className="mb-4">
                                        <Col>
                                            <div className="text-center mb-4">
                                                <p className="text-white-50">
                                                    Loading your selected leagues...
                                                </p>
                                            </div>
                                        </Col>
                                    </Row>
                                )}

                                {/* Main Plan Card */}
                                {selectedLeagues.length > 0 && (
                                    <Row className="mt-3 mb-3">
                                        <Col>
                                            <div className="text-center">
                                                <div className="glass-card onboarding-plan-card" style={{ padding: '1.5rem 2.5rem' }}>
                                                    <div style={{ textAlign: 'left' }}>
                                                        <h4 className="text-white mb-2" style={{ textAlign: 'left' }}>
                                                            <span className="plan-title-desktop">Weekly Fantasy League Recaps</span>
                                                            <span className="plan-title-mobile">Weekly Recaps</span>
                                                        </h4>
                                                        
                                                        {/* Pricing - Moved to top */}
                                                        <div className="mb-2" style={{ textAlign: 'left' }}>
                                                            <div className="text-white" style={{ fontSize: '3rem', fontWeight: 'bold', color: '#3b82f6', textAlign: 'left' }}>
                                                                ${selectedLeagues.length * 10} <span style={{ fontSize: '1.1rem', color: '#94a3b8' }}>per month</span>
                                                            </div>
                                                            <div className="text-warning" style={{ fontSize: '0.9rem', fontWeight: '500', textAlign: 'left', marginTop: '4px' }}>
                                                                💰 Get $10 Venmo credit - contact founder in Discord after payment
                                                            </div>
                                                        </div>
                                                        
                                                                                       {/* Benefits */}
                               <div className="mb-4" style={{ textAlign: 'left' }}>
                                   <div className="text-white-50 mb-2 d-flex align-items-center" style={{ textAlign: 'left' }}>
                                       <i className="fas fa-video" style={{ flexShrink: 0, width: '20px', marginRight: '12px' }}></i>
                                       <span className="plan-benefit-1-desktop">2-minute personalized video</span>
                                       <span className="plan-benefit-1-mobile">2-min video</span>
                                   </div>
                                   <div className="text-white-50 mb-2 d-flex align-items-center" style={{ textAlign: 'left' }}>
                                       <i className="fas fa-calendar" style={{ flexShrink: 0, width: '20px', marginRight: '12px' }}></i>
                                       <span className="plan-benefit-2-desktop">Every Tuesday BY 1pm ET</span>
                                       <span className="plan-benefit-2-mobile">Every Tue BY 1pm ET</span>
                                   </div>
                                   <div className="text-white-50 mb-2 d-flex align-items-center" style={{ textAlign: 'left' }}>
                                       <i className="fas fa-trophy" style={{ flexShrink: 0, width: '20px', marginRight: '12px' }}></i>
                                       <span className="plan-benefit-3-desktop">All matchups in your league</span>
                                       <span className="plan-benefit-3-mobile">All matchups</span>
                                   </div>
                                   <div className="text-white-50 d-flex align-items-center" style={{ textAlign: 'left' }}>
                                       <i className="fas fa-share-alt" style={{ flexShrink: 0, width: '20px', marginRight: '12px' }}></i>
                                       <span className="plan-benefit-4-desktop">Download and share anywhere</span>
                                       <span className="plan-benefit-4-mobile">Download and share</span>
                                   </div>
                               </div>
                                                        
                                                                                       {/* Pay Now Button */}
                               <button 
                                   className="btn btn-primary-gradient btn-lg"
                                   style={{ 
                                       fontSize: '1.2rem', 
                                       padding: '12px 32px',
                                       borderRadius: '12px',
                                       border: 'none',
                                       background: 'linear-gradient(135deg, #3b82f6 0%, #8b5cf6 100%)',
                                       color: 'white',
                                       fontWeight: '600',
                                       textAlign: 'center'
                                   }}
                                   onClick={handlePaymentSubmit}
                                   disabled={isPaymentLoading}
                               >
                                   {isPaymentLoading ? (
                                       <>
                                           <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                           Processing...
                                       </>
                                   ) : (
                                       'Pay Now'
                                   )}
                               </button>
                                                    </div>
                                                </div>
                                            </div>
                                        </Col>
                                    </Row>
                                )}

                                {/* Discord Helper Text */}
                                <Row className="mt-3">
                                    <Col>
                                        <div className="text-center">
                                            <p className="text-white-50" style={{ fontSize: '0.9rem' }}>
                                                Have a question? Talk to our founder in{' '}
                                                <a 
                                                    href={appConfig.social.discord} 
                                                    target="_blank" 
                                                    rel="noopener noreferrer"
                                                    style={{ color: '#5865f2', textDecoration: 'none' }}
                                                >
                                                    Discord
                                                </a>.
                                            </p>
                                        </div>
                                    </Col>
                                </Row>
                            </Col>
                        </Row>                        
                    </Col>                            
                </Row>
            </div>
        </>
    );
};

function mapStateToProps({auth}) {
    return { auth };
}

export default connect(mapStateToProps)(PlanSelection);
